<template>
  <dashboard-layout title="Export">
    <data-panel
      title="Chat Messages Export"
      subtitle="Export user messages from September 18, 2025 to October 18, 2025"
    >
      <div class="card-body">
        <button
          @click="exportChatMessages"
          :disabled="loading"
          class="export-button"
        >
          <svg v-if="!loading" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          <div v-else class="spinner"></div>
          {{ loading ? 'Exporting...' : 'Export Chat Messages' }}
        </button>

        <div v-if="status" class="status-message" :class="{ error: isError }">
          {{ status }}
        </div>
      </div>
    </data-panel>

    <data-panel
      title="Users with Min Messages Export"
      subtitle="Export detailed user data (all chats)"
    >
      <div class="card-body">
        <div class="input-group">
          <label for="minMessages">Minimum Messages:</label>
          <input
            id="minMessages"
            v-model.number="minMessages"
            type="number"
            min="1"
            :disabled="loading2"
            class="min-messages-input"
          />
        </div>

        <button
          @click="exportUsersWithMinMessages"
          :disabled="loading2"
          class="export-button"
        >
          <svg v-if="!loading2" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          <div v-else class="spinner"></div>
          {{ loading2 ? 'Exporting...' : `Export Users with Min ${minMessages} Messages` }}
        </button>

        <div v-if="status2" class="status-message" :class="{ error: isError2 }">
          {{ status2 }}
        </div>

        <div v-if="loading2 && progress2.total > 0" class="progress-container">
          <div class="progress-info">
            <span class="progress-text">
              Processing user {{ progress2.current }} of {{ progress2.total }}
            </span>
            <span v-if="progress2.estimatedTimeRemaining" class="time-remaining">
              Estimated time remaining: {{ formatTime(progress2.estimatedTimeRemaining) }}
            </span>
          </div>
          <div class="progress-bar-wrapper">
            <div
              class="progress-bar-fill"
              :style="{ width: `${(progress2.current / progress2.total) * 100}%` }"
            ></div>
          </div>
          <div class="progress-percentage">
            {{ Math.round((progress2.current / progress2.total) * 100) }}%
          </div>
        </div>
      </div>
    </data-panel>

    <data-panel
      title="Users Export by Date Range"
      subtitle="Export detailed user data within a specific date range"
    >
      <div class="card-body">
        <div class="date-range-inputs">
          <div class="input-group">
            <label for="startDate">Start Date:</label>
            <input
              id="startDate"
              v-model="startDate"
              type="date"
              :disabled="loading4"
              class="date-input"
            />
          </div>
          <div class="input-group">
            <label for="endDate">End Date:</label>
            <input
              id="endDate"
              v-model="endDate"
              type="date"
              :disabled="loading4"
              class="date-input"
            />
          </div>
        </div>

        <button
          @click="exportUsersByDateRange"
          :disabled="loading4 || !startDate || !endDate"
          class="export-button"
        >
          <svg v-if="!loading4" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          <div v-else class="spinner"></div>
          {{ loading4 ? 'Exporting...' : 'Export Users by Date Range' }}
        </button>

        <div v-if="status4" class="status-message" :class="{ error: isError4 }">
          {{ status4 }}
        </div>

        <div v-if="loading4 && progress4.total > 0" class="progress-container">
          <div class="progress-info">
            <span class="progress-text">
              Processing user {{ progress4.current }} of {{ progress4.total }}
            </span>
            <span v-if="progress4.estimatedTimeRemaining" class="time-remaining">
              Estimated time remaining: {{ formatTime(progress4.estimatedTimeRemaining) }}
            </span>
          </div>
          <div class="progress-bar-wrapper">
            <div
              class="progress-bar-fill"
              :style="{ width: `${(progress4.current / progress4.total) * 100}%` }"
            ></div>
          </div>
          <div class="progress-percentage">
            {{ Math.round((progress4.current / progress4.total) * 100) }}%
          </div>
        </div>
      </div>
    </data-panel>

    <data-panel
      title="Recent Users Questions Export"
      subtitle="Export recent 500 users with their questions (sorted by date)"
    >
      <div class="card-body">
        <button
          @click="exportRecentUsersQuestions"
          :disabled="loading3"
          class="export-button"
        >
          <svg v-if="!loading3" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          <div v-else class="spinner"></div>
          {{ loading3 ? 'Exporting...' : 'Export Recent Users Questions' }}
        </button>

        <div v-if="status3" class="status-message" :class="{ error: isError3 }">
          {{ status3 }}
        </div>

        <div v-if="loading3 && progress3.total > 0" class="progress-container">
          <div class="progress-info">
            <span class="progress-text">
              Processing chat {{ progress3.current }} of {{ progress3.total }}
            </span>
            <span v-if="progress3.estimatedTimeRemaining" class="time-remaining">
              Estimated time remaining: {{ formatTime(progress3.estimatedTimeRemaining) }}
            </span>
          </div>
          <div class="progress-bar-wrapper">
            <div
              class="progress-bar-fill"
              :style="{ width: `${(progress3.current / progress3.total) * 100}%` }"
            ></div>
          </div>
          <div class="progress-percentage">
            {{ Math.round((progress3.current / progress3.total) * 100) }}%
          </div>
        </div>
      </div>
    </data-panel>

    <data-panel
      title="Chat Messages by User Creation Date"
      subtitle="Export all chat messages of users created within a date range"
    >
      <div class="card-body">
        <div class="date-range-inputs">
          <div class="input-group">
            <label for="chatStartDate">Start Date:</label>
            <input
              id="chatStartDate"
              v-model="chatExportStartDate"
              type="date"
              :disabled="loading5"
              class="date-input"
            />
          </div>
          <div class="input-group">
            <label for="chatEndDate">End Date:</label>
            <input
              id="chatEndDate"
              v-model="chatExportEndDate"
              type="date"
              :disabled="loading5"
              class="date-input"
            />
          </div>
        </div>

        <div class="checkbox-group">
          <label class="checkbox-label">
            <input type="checkbox" v-model="includeUserMessages" :disabled="loading5">
            <span>User messages (questions)</span>
          </label>
          <label class="checkbox-label">
            <input type="checkbox" v-model="includeAssistantMessages" :disabled="loading5">
            <span>Assistant messages (answers)</span>
          </label>
          <label class="checkbox-label">
            <input type="checkbox" v-model="includeSystemMessages" :disabled="loading5">
            <span>System prompts</span>
          </label>
        </div>

        <div class="export-buttons-row">
          <button
            @click="exportChatByDateRange"
            :disabled="loading5 || !chatExportStartDate || !chatExportEndDate || (!includeUserMessages && !includeAssistantMessages && !includeSystemMessages)"
            class="export-button"
          >
            <svg v-if="!loading5" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            <div v-else class="spinner"></div>
            {{ loading5 ? 'Exporting...' : 'Export CSV' }}
          </button>
          <button
            @click="exportChatByDateRangeJSON"
            :disabled="loading5 || !chatExportStartDate || !chatExportEndDate || (!includeUserMessages && !includeAssistantMessages && !includeSystemMessages)"
            class="export-button json-btn"
          >
            <svg v-if="!loading5" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            <div v-else class="spinner"></div>
            {{ loading5 ? 'Exporting...' : 'Export JSON' }}
          </button>
        </div>

        <div v-if="status5" class="status-message" :class="{ error: isError5 }">
          {{ status5 }}
        </div>

        <div v-if="loading5 && progress5.total > 0" class="progress-container">
          <div class="progress-info">
            <span class="progress-text">
              Processing user {{ progress5.current }} of {{ progress5.total }}
            </span>
            <span v-if="progress5.estimatedTimeRemaining" class="time-remaining">
              Estimated time remaining: {{ formatTime(progress5.estimatedTimeRemaining) }}
            </span>
          </div>
          <div class="progress-bar-wrapper">
            <div
              class="progress-bar-fill"
              :style="{ width: `${(progress5.current / progress5.total) * 100}%` }"
            ></div>
          </div>
          <div class="progress-percentage">
            {{ Math.round((progress5.current / progress5.total) * 100) }}%
          </div>
        </div>
      </div>
    </data-panel>
  </dashboard-layout>
</template>

<script>
import { ref } from 'vue'
import { collection, query, orderBy, getDocs, where } from 'firebase/firestore'
import { db } from '../firebase'
import { FirebaseChatExportService } from '../services/firebaseChatExportService'
import DashboardLayout from './ui/DashboardLayout.vue'
import DataPanel from './ui/DataPanel.vue'

export default {
  name: 'ExportDashboard',
  components: {
    DashboardLayout,
    DataPanel
  },
  setup() {
    const service = new FirebaseChatExportService()
    const loading = ref(false)
    const status = ref('')
    const isError = ref(false)

    const loading2 = ref(false)
    const status2 = ref('')
    const isError2 = ref(false)
    const minMessages = ref(5)
    const progress2 = ref({
      current: 0,
      total: 0,
      estimatedTimeRemaining: null,
      processingTime: null
    })

    const loading3 = ref(false)
    const status3 = ref('')
    const isError3 = ref(false)
    const progress3 = ref({
      current: 0,
      total: 0,
      estimatedTimeRemaining: null,
      processingTime: null
    })

    // Date range export state
    const loading4 = ref(false)
    const status4 = ref('')
    const isError4 = ref(false)
    const startDate = ref('')
    const endDate = ref('')
    const progress4 = ref({
      current: 0,
      total: 0,
      estimatedTimeRemaining: null,
      processingTime: null
    })

    // Chat by date range export state
    const loading5 = ref(false)
    const status5 = ref('')
    const isError5 = ref(false)
    const chatExportStartDate = ref('')
    const chatExportEndDate = ref('')
    const includeUserMessages = ref(true)
    const includeAssistantMessages = ref(true)
    const includeSystemMessages = ref(false)
    const progress5 = ref({
      current: 0,
      total: 0,
      estimatedTimeRemaining: null
    })

    const exportChatMessages = async () => {
      loading.value = true
      status.value = ''
      isError.value = false

      try {
        const startDate = new Date('2025-09-18T00:00:00Z')
        const endDate = new Date('2025-10-18T23:59:59Z')

        status.value = 'Fetching last 500 chats from Firebase...'
        const messages = await service.getMessagesInDateRange(startDate, endDate)

        if (!messages || messages.length === 0) {
          status.value = 'No messages found in the specified date range.'
          isError.value = true
          loading.value = false
          return
        }

        status.value = `Found ${messages.length} messages. Generating CSV...`

        const headers = ['User ID', 'Gender', 'Age', 'Message Content', 'Created At']

        const escapeCSV = (val) => {
          if (val === null || val === undefined) return ''
          let str = String(val)
          str = str.replace(/\r?\n/g, ' ')
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            str = '"' + str.replace(/"/g, '""') + '"'
          }
          return str
        }

        const formatTimestamp = (ts) => {
          if (!ts) return ''
          if (ts.seconds) {
            const d = new Date(ts.seconds * 1000)
            return d.toLocaleString('tr-TR', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
            })
          }
          const d = new Date(ts)
          return d.toLocaleString('tr-TR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          })
        }

        const rows = messages.map(msg => {
          return [
            escapeCSV(msg.userId),
            escapeCSV(msg.gender || '-'),
            escapeCSV(msg.age !== null ? msg.age : '-'),
            escapeCSV(msg.content),
            escapeCSV(formatTimestamp(msg.createdAt))
          ].join(',')
        })

        const csvContent = [headers.join(','), ...rows].join('\n')
        const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.setAttribute('download', `chat_messages_export_${Date.now()}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        status.value = `Successfully exported ${messages.length} messages!`
        isError.value = false
      } catch (error) {
        console.error('Export error:', error)
        status.value = `Error: ${error.message}`
        isError.value = true
      } finally {
        loading.value = false
      }
    }

    const formatTime = (seconds) => {
      if (seconds < 60) {
        return `${seconds}s`
      }
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = seconds % 60
      return `${minutes}m ${remainingSeconds}s`
    }

    const exportUsersByDateRange = async () => {
      loading4.value = true
      status4.value = ''
      isError4.value = false
      progress4.value = {
        current: 0,
        total: 0,
        estimatedTimeRemaining: null,
        processingTime: null
      }

      try {
        const start = new Date(startDate.value)
        start.setHours(0, 0, 0, 0)
        const end = new Date(endDate.value)
        end.setHours(23, 59, 59, 999)

        status4.value = 'Fetching users and chats from Firebase...'

        const users = await service.getUsersInDateRange(start, end, (progressData) => {
          progress4.value = progressData
        })

        if (!users || users.length === 0) {
          status4.value = 'No users found in the specified date range.'
          isError4.value = true
          loading4.value = false
          return
        }

        status4.value = `Found ${users.length} users. Generating CSV...`

        const headers = [
          'User ID',
          'Gender',
          'Age',
          'Sun Sign',
          'Moon Sign',
          'Ascendant Sign',
          'Why User Came',
          'Why User Not Purchase',
          'Keyword',
          'Campaign Name',
          'Messages',
          'LTV',
          'Subscription Status',
          'Plan Type',
          'Product ID',
          'Activated At',
          'Expires At',
          'Store',
          'Cancellation Reason',
          'Cancel Feedback'
        ]

        const escapeCSV = (val) => {
          if (val === null || val === undefined) return ''
          let str = String(val)
          str = str.replace(/\r?\n/g, ' ')
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            str = '"' + str.replace(/"/g, '""') + '"'
          }
          return str
        }

        const rows = users.map(user => {
          return [
            escapeCSV(user.userId),
            escapeCSV(user.gender || '-'),
            escapeCSV(user.age !== null ? user.age : '-'),
            escapeCSV(user.sunSign || '-'),
            escapeCSV(user.moonSign || '-'),
            escapeCSV(user.ascendantSign || '-'),
            escapeCSV(user.whyUserCame || '-'),
            escapeCSV(user.whyUserNotPurchase || '-'),
            escapeCSV(user.keyword || '-'),
            escapeCSV(user.campaignName || '-'),
            escapeCSV(user.messages),
            escapeCSV(user.ltv || '-'),
            escapeCSV(user.subscriptionStatus || '-'),
            escapeCSV(user.planType || '-'),
            escapeCSV(user.productId || '-'),
            escapeCSV(user.activatedAt || '-'),
            escapeCSV(user.expiresAt || '-'),
            escapeCSV(user.store || '-'),
            escapeCSV(user.cancellationReason || '-'),
            escapeCSV(user.cancelFeedback || '-')
          ].join(',')
        })

        const csvContent = [headers.join(','), ...rows].join('\n')
        const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        const formattedStart = startDate.value.replace(/-/g, '')
        const formattedEnd = endDate.value.replace(/-/g, '')
        link.setAttribute('download', `users_export_${formattedStart}_${formattedEnd}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        status4.value = `Successfully exported ${users.length} users!`
        isError4.value = false
      } catch (error) {
        console.error('Export error:', error)
        status4.value = `Error: ${error.message}`
        isError4.value = true
      } finally {
        loading4.value = false
      }
    }

    const exportRecentUsersQuestions = async () => {
      loading3.value = true
      status3.value = ''
      isError3.value = false
      progress3.value = {
        current: 0,
        total: 0,
        estimatedTimeRemaining: null,
        processingTime: null
      }

      try {
        status3.value = 'Fetching recent 500 users from Firebase...'

        const usersData = await service.getRecentUsersWithQuestions(500, (progressData) => {
          progress3.value = progressData
        })

        if (!usersData || usersData.length === 0) {
          status3.value = 'No users found.'
          isError3.value = true
          loading3.value = false
          return
        }

        status3.value = `Found ${usersData.length} users. Generating CSV...`

        const maxQuestions = Math.max(...usersData.map(u => u.questions.length))

        const headers = ['User ID', 'Name', 'IDFV', 'Gender', 'Age', 'Sign', 'Why User Not Purchase', 'Created At']
        for (let i = 1; i <= maxQuestions; i++) {
          headers.push(`Question ${i}`)
        }

        const escapeCSV = (val) => {
          if (val === null || val === undefined) return ''
          let str = String(val)
          str = str.replace(/"/g, '""')
          if (str.includes(',') || str.includes('\n') || str.includes('"')) {
            str = `"${str}"`
          }
          return str
        }

        const rows = usersData.map(user => {
          const row = [
            escapeCSV(user.userId),
            escapeCSV(user.name),
            escapeCSV(user.idfv),
            escapeCSV(user.gender),
            escapeCSV(user.age),
            escapeCSV(user.sign),
            escapeCSV(user.whyUserNotPurchase),
            escapeCSV(user.createdAt)
          ]

          for (let i = 0; i < maxQuestions; i++) {
            row.push(escapeCSV(user.questions[i] || ''))
          }

          return row.join(',')
        })

        const csvContent = [headers.join(','), ...rows].join('\n')
        const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.setAttribute('download', `recent_users_questions_${Date.now()}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        status3.value = `Successfully exported ${usersData.length} users!`
      } catch (error) {
        console.error('Export error:', error)
        status3.value = `Error: ${error.message}`
        isError3.value = true
      } finally {
        loading3.value = false
      }
    }

    const exportUsersWithMinMessages = async () => {
      loading2.value = true
      status2.value = ''
      isError2.value = false
      progress2.value = {
        current: 0,
        total: 0,
        estimatedTimeRemaining: null,
        processingTime: null
      }

      try {
        status2.value = 'Fetching all chats from Firebase...'

        const users = await service.getUsersWithMinMessages(null, minMessages.value, (progressData) => {
          progress2.value = progressData
        })

        if (!users || users.length === 0) {
          status2.value = `No users found with minimum ${minMessages.value} messages.`
          isError2.value = true
          loading2.value = false
          return
        }

        status2.value = `Found ${users.length} users. Generating CSV...`

        const headers = [
          'User ID',
          'Gender',
          'Age',
          'Sun Sign',
          'Moon Sign',
          'Ascendant Sign',
          'Why User Came',
          'Why User Not Purchase',
          'Keyword',
          'Campaign Name',
          'Messages',
          'LTV',
          'Cancellation Reason'
        ]

        const escapeCSV = (val) => {
          if (val === null || val === undefined) return ''
          let str = String(val)
          str = str.replace(/\r?\n/g, ' ')
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            str = '"' + str.replace(/"/g, '""') + '"'
          }
          return str
        }

        const rows = users.map(user => {
          return [
            escapeCSV(user.userId),
            escapeCSV(user.gender || '-'),
            escapeCSV(user.age !== null ? user.age : '-'),
            escapeCSV(user.sunSign || '-'),
            escapeCSV(user.moonSign || '-'),
            escapeCSV(user.ascendantSign || '-'),
            escapeCSV(user.whyUserCame || '-'),
            escapeCSV(user.whyUserNotPurchase || '-'),
            escapeCSV(user.keyword || '-'),
            escapeCSV(user.campaignName || '-'),
            escapeCSV(user.messages),
            escapeCSV(user.ltv || '-'),
            escapeCSV(user.cancellationReason || '-')
          ].join(',')
        })

        const csvContent = [headers.join(','), ...rows].join('\n')
        const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.setAttribute('download', `users_with_min_messages_export_${Date.now()}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        status2.value = `Successfully exported ${users.length} users!`
        isError2.value = false
      } catch (error) {
        console.error('Export error:', error)
        status2.value = `Error: ${error.message}`
        isError2.value = true
      } finally {
        loading2.value = false
      }
    }

    const fetchUsersInRange = async (start, end) => {
      const usersRef = collection(db, 'users')
      const usersQuery = query(usersRef, orderBy('createdAt', 'desc'))
      const usersSnap = await getDocs(usersQuery)
      const matchedUserIds = []
      usersSnap.forEach(doc => {
        const data = doc.data()
        const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt?.seconds ? new Date(data.createdAt.seconds * 1000) : null)
        if (createdAt && createdAt >= start && createdAt <= end) {
          matchedUserIds.push(doc.id)
        }
      })
      return matchedUserIds
    }

    const exportChatByDateRange = async () => {
      loading5.value = true
      status5.value = ''
      isError5.value = false
      progress5.value = { current: 0, total: 0, estimatedTimeRemaining: null }

      try {
        const start = new Date(chatExportStartDate.value)
        start.setHours(0, 0, 0, 0)
        const end = new Date(chatExportEndDate.value)
        end.setHours(23, 59, 59, 999)

        status5.value = 'Fetching users in date range...'
        const matchedUserIds = await fetchUsersInRange(start, end)

        if (matchedUserIds.length === 0) {
          status5.value = 'No users found in the specified date range.'
          isError5.value = true
          loading5.value = false
          return
        }

        status5.value = `Found ${matchedUserIds.length} users. Fetching chats...`

        const chatsRef = collection(db, 'chats')
        const allMessages = []
        const startTime = Date.now()
        const rolesToInclude = []
        if (includeUserMessages.value) rolesToInclude.push('user')
        if (includeAssistantMessages.value) rolesToInclude.push('assistant')
        if (includeSystemMessages.value) rolesToInclude.push('system')

        progress5.value.total = matchedUserIds.length

        for (let i = 0; i < matchedUserIds.length; i++) {
          const userId = matchedUserIds[i]
          const chatQuery = query(chatsRef, where('userId', '==', userId))
          const chatSnap = await getDocs(chatQuery)

          const [userInfo, adaptyData] = await Promise.all([
            service.getUserInfo(userId),
            service.getAdaptyProfile(userId)
          ])

          chatSnap.forEach(doc => {
            const messages = doc.data().messages || []
            for (const msg of messages) {
              if (!rolesToInclude.includes(msg.role)) continue
              allMessages.push({
                userId,
                role: msg.role,
                content: msg.originalContent || msg.content || '',
                createdAt: msg.createdAt,
                gender: userInfo.gender,
                age: userInfo.age,
                feedbackThumb: msg.feedbackThumb || '',
                suggestedQuestions: Array.isArray(msg.suggestedQuestions) ? msg.suggestedQuestions.join(' | ') : '',
                toolsUsed: Array.isArray(msg.toolsUsed) ? msg.toolsUsed.join(', ') : (msg.toolsUsed || ''),
                isReengagement: msg.isReengagement || false,
                isUserChartUpdated: msg.isUserChartUpdated || false,
                messageId: msg.messageId || '',
                ltv: adaptyData.ltv,
                subscriptionStatus: adaptyData.subscriptionStatus,
                planType: adaptyData.planType,
                productId: adaptyData.productId
              })
            }
          })

          progress5.value.current = i + 1
          const elapsed = (Date.now() - startTime) / 1000
          const rate = (i + 1) / elapsed
          const remaining = Math.round((matchedUserIds.length - i - 1) / rate)
          progress5.value.estimatedTimeRemaining = remaining
        }

        if (allMessages.length === 0) {
          status5.value = `Found ${matchedUserIds.length} users but no messages matching the filters.`
          isError5.value = true
          loading5.value = false
          return
        }

        status5.value = `Found ${allMessages.length} messages from ${matchedUserIds.length} users. Generating CSV...`

        const escapeCSV = (val) => {
          if (val === null || val === undefined) return ''
          let str = String(val)
          str = str.replace(/\r?\n/g, ' ')
          if (str.includes(',') || str.includes('"')) {
            str = '"' + str.replace(/"/g, '""') + '"'
          }
          return str
        }

        const fmtTs = (ts) => {
          if (!ts) return ''
          const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts)
          return d.toLocaleString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })
        }

        const headers = ['User ID', 'Role', 'Gender', 'Age', 'Message Content', 'Created At', 'Feedback', 'Suggested Questions', 'Tools Used', 'Reengagement', 'Chart Updated', 'Message ID', 'LTV', 'Subscription Status', 'Plan Type', 'Product ID']
        const rows = allMessages.map(msg => [
          escapeCSV(msg.userId),
          escapeCSV(msg.role),
          escapeCSV(msg.gender || '-'),
          escapeCSV(msg.age !== null ? msg.age : '-'),
          escapeCSV(msg.content),
          escapeCSV(fmtTs(msg.createdAt)),
          escapeCSV(msg.feedbackThumb),
          escapeCSV(msg.suggestedQuestions),
          escapeCSV(msg.toolsUsed),
          escapeCSV(msg.isReengagement ? 'Yes' : ''),
          escapeCSV(msg.isUserChartUpdated ? 'Yes' : ''),
          escapeCSV(msg.messageId),
          escapeCSV(msg.ltv || '-'),
          escapeCSV(msg.subscriptionStatus || '-'),
          escapeCSV(msg.planType || '-'),
          escapeCSV(msg.productId || '-')
        ].join(','))

        const csvContent = [headers.join(','), ...rows].join('\n')
        const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.setAttribute('download', `chat_by_users_${chatExportStartDate.value}_${chatExportEndDate.value}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        status5.value = `Successfully exported ${allMessages.length} messages from ${matchedUserIds.length} users!`
      } catch (error) {
        console.error('Export error:', error)
        status5.value = `Error: ${error.message}`
        isError5.value = true
      } finally {
        loading5.value = false
      }
    }

    const exportChatByDateRangeJSON = async () => {
      loading5.value = true
      status5.value = ''
      isError5.value = false
      progress5.value = { current: 0, total: 0, estimatedTimeRemaining: null }

      try {
        const start = new Date(chatExportStartDate.value)
        start.setHours(0, 0, 0, 0)
        const end = new Date(chatExportEndDate.value)
        end.setHours(23, 59, 59, 999)

        status5.value = 'Fetching users in date range...'
        const matchedUserIds = await fetchUsersInRange(start, end)

        if (matchedUserIds.length === 0) {
          status5.value = 'No users found in the specified date range.'
          isError5.value = true
          loading5.value = false
          return
        }

        status5.value = `Found ${matchedUserIds.length} users. Fetching chats...`

        const chatsRef = collection(db, 'chats')
        const usersMap = {}
        const startTime = Date.now()
        const rolesToInclude = []
        if (includeUserMessages.value) rolesToInclude.push('user')
        if (includeAssistantMessages.value) rolesToInclude.push('assistant')
        if (includeSystemMessages.value) rolesToInclude.push('system')

        const fmtTs = (ts) => {
          if (!ts) return null
          const d = ts.seconds ? new Date(ts.seconds * 1000) : (ts.toDate ? ts.toDate() : new Date(ts))
          return d.toISOString()
        }

        progress5.value.total = matchedUserIds.length

        for (let i = 0; i < matchedUserIds.length; i++) {
          const userId = matchedUserIds[i]
          const chatQuery = query(chatsRef, where('userId', '==', userId))
          const chatSnap = await getDocs(chatQuery)

          const [userInfo, adaptyData] = await Promise.all([
            service.getUserInfo(userId),
            service.getAdaptyProfile(userId)
          ])

          chatSnap.forEach(doc => {
            const messages = doc.data().messages || []
            for (const msg of messages) {
              if (!rolesToInclude.includes(msg.role)) continue

              if (!usersMap[userId]) {
                usersMap[userId] = {
                  userId,
                  gender: userInfo.gender || null,
                  age: userInfo.age !== null ? userInfo.age : null,
                  subscription: {
                    ltv: adaptyData.ltv,
                    status: adaptyData.subscriptionStatus,
                    planType: adaptyData.planType,
                    productId: adaptyData.productId,
                    activatedAt: adaptyData.activatedAt,
                    expiresAt: adaptyData.expiresAt,
                    store: adaptyData.store,
                    cancellationReason: adaptyData.cancellationReason
                  },
                  messages: []
                }
              }

              usersMap[userId].messages.push({
                role: msg.role,
                content: msg.originalContent || msg.content || '',
                createdAt: fmtTs(msg.createdAt),
                feedbackThumb: msg.feedbackThumb || null,
                suggestedQuestions: msg.suggestedQuestions || null,
                toolsUsed: msg.toolsUsed || null,
                isReengagement: msg.isReengagement || false,
                isUserChartUpdated: msg.isUserChartUpdated || false,
                messageId: msg.messageId || null
              })
            }
          })

          progress5.value.current = i + 1
          const elapsed = (Date.now() - startTime) / 1000
          const rate = (i + 1) / elapsed
          const remaining = Math.round((matchedUserIds.length - i - 1) / rate)
          progress5.value.estimatedTimeRemaining = remaining
        }

        const usersArray = Object.values(usersMap)
        if (usersArray.length === 0) {
          status5.value = `Found ${matchedUserIds.length} users but no messages matching the filters.`
          isError5.value = true
          loading5.value = false
          return
        }

        const totalMessages = usersArray.reduce((sum, u) => sum + u.messages.length, 0)
        status5.value = `Found ${totalMessages} messages from ${usersArray.length} users. Generating JSON...`

        const jsonContent = JSON.stringify(usersArray, null, 2)
        const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.setAttribute('download', `chat_by_users_${chatExportStartDate.value}_${chatExportEndDate.value}.json`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        status5.value = `Successfully exported ${totalMessages} messages from ${usersArray.length} users as JSON!`
      } catch (error) {
        console.error('JSON Export error:', error)
        status5.value = `Error: ${error.message}`
        isError5.value = true
      } finally {
        loading5.value = false
      }
    }

    return {
      loading,
      status,
      isError,
      exportChatMessages,
      loading2,
      status2,
      isError2,
      minMessages,
      progress2,
      formatTime,
      exportUsersWithMinMessages,
      loading3,
      status3,
      isError3,
      progress3,
      exportRecentUsersQuestions,
      loading4,
      status4,
      isError4,
      startDate,
      endDate,
      progress4,
      exportUsersByDateRange,
      loading5,
      status5,
      isError5,
      chatExportStartDate,
      chatExportEndDate,
      includeUserMessages,
      includeAssistantMessages,
      includeSystemMessages,
      progress5,
      exportChatByDateRange,
      exportChatByDateRangeJSON
    }
  }
}
</script>

<style scoped>
.card-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.input-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.input-group label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
}

.min-messages-input {
  padding: 8px 12px;
  background: rgba(108, 99, 255, 0.05);
  border: 1px solid rgba(108, 99, 255, 0.2);
  border-radius: 6px;
  color: var(--text-primary);
  font-size: 13px;
  font-weight: 500;
  width: 80px;
  transition: all 0.2s ease;
}

.min-messages-input:focus {
  outline: none;
  border-color: #6c63ff;
  background: rgba(108, 99, 255, 0.1);
}

.min-messages-input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.date-range-inputs {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.date-input {
  padding: 8px 12px;
  background: rgba(108, 99, 255, 0.05);
  border: 1px solid rgba(108, 99, 255, 0.2);
  border-radius: 6px;
  color: var(--text-primary);
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
  color-scheme: dark;
}

.date-input:focus {
  outline: none;
  border-color: #6c63ff;
  background: rgba(108, 99, 255, 0.1);
}

.date-input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.checkbox-group {
  display: flex;
  gap: 16px;
  margin: 12px 0;
  flex-wrap: wrap;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 13px;
  color: var(--text-secondary);
}

.checkbox-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #6c63ff;
  cursor: pointer;
}

.checkbox-label span {
  user-select: none;
}

.export-buttons-row {
  display: flex;
  gap: 12px;
}
.export-buttons-row .export-button {
  flex: 1;
}
.json-btn {
  background: linear-gradient(135deg, #22d3ae, #1a9d82) !important;
}
.json-btn:hover:not(:disabled) {
  box-shadow: 0 4px 15px rgba(34, 211, 174, 0.3) !important;
}
.export-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #6c63ff, #5a52d5);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  width: fit-content;
}
.export-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #5a52d5, #4a42c5);
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(108, 99, 255, 0.35);
}
.export-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
.status-message {
  padding: 10px 14px;
  background: rgba(108, 99, 255, 0.1);
  border: 1px solid #6c63ff;
  border-radius: 6px;
  color: #6c63ff;
  font-size: 13px;
}
.status-message.error {
  background: rgba(220, 53, 69, 0.1);
  border-color: #dc3545;
  color: #dc3545;
}

.progress-container {
  margin-top: 14px;
  padding: 14px;
  background: rgba(108, 99, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(108, 99, 255, 0.2);
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  flex-wrap: wrap;
  gap: 8px;
}

.progress-text {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.time-remaining {
  font-size: 13px;
  color: #6c63ff;
  font-weight: 500;
}

.progress-bar-wrapper {
  width: 100%;
  height: 6px;
  background: rgba(108, 99, 255, 0.1);
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 6px;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #6c63ff, #5a52d5);
  border-radius: 6px;
  transition: width 0.3s ease;
  box-shadow: 0 0 8px rgba(108, 99, 255, 0.4);
}

.progress-percentage {
  text-align: right;
  font-size: 13px;
  font-weight: 600;
  color: #6c63ff;
}

@media (max-width: 1024px) {
  .export-button {
    width: 100%;
  }
}
</style>
